import { FileSyncOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  message,
  Popover,
  Spin,
  Tooltip,
} from 'antd';
import React, { useEffect, useState } from 'react';

import useTable from '../../components/Template/TableTemplate';
import { useOrderIsAutoPayment } from '../../custom-hooks/sa-orders';
import { fetchAPI } from '../../utils/api';

const OrderItemToSelect = ({ record, reloadOrderList, hide, visible }) => {
  const [loading, setLoading] = useState(false);
  const [table, toggleTable] = useTable({
    paginationPosition: 'bottom',
    showDateRange: false,
    showDownload: false,
    url: `order/${record.id}/item-list`,
    method: 'GET',
    showSearch: false,
    updateQueryParams: false,
    showPagination: false,
    tableProps: {
      scroll: { y: 'calc(100vh - 345px)' },
    },
    columns: [
      {
        title: 'Supplier',
        key: 'supplier',
        sorter: true,
        width: 110,
        render: itemRecord => {
          return (
            <>
              <span className="m-label">Supplier</span>
              {itemRecord?.supplier?.name}
            </>
          );
        },
      },
      {
        title: 'Item',
        key: 'item',
        sorter: true,
        width: 110,
        render: itemRecord => {
          return (
            <>
              <span className="m-label">Item</span>
              {itemRecord?.name}
            </>
          );
        },
      },
      {
        title: 'Price',
        key: 'price',
        sorter: false,
        width: 110,
        render: itemRecord => {
          return (
            <>
              <span className="m-label">Price</span>
              {itemRecord.price}
            </>
          );
        },
      },
      {
        title: 'Quantity',
        key: 'quantity',
        sorter: false,
        width: 110,
        render: itemRecord => {
          return (
            <>
              <span className="m-label">Quantity</span>
              {itemRecord.quantity}
            </>
          );
        },
      },
      {
        title: 'Can Auto Create SPID',
        key: 'can_auto_create_spid',
        sorter: false,
        width: 110,
        render: itemRecord => {
          return (
            <>
              <span className="m-label">Can Auto Create SPID</span>
              {itemRecord.can_auto_create_spid ? (
                <span>YES</span>
              ) : (
                <span>NO</span>
              )}
            </>
          );
        },
      },
    ],
  });

  const orderAutoPayment = useOrderIsAutoPayment(
    record.id,
    visible,
    record.is_auto_payment
  );

  useEffect(() => {
    toggleTable();
  }, [record.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [invoiceDate, setInvoiceDate] = useState('');

  const postGenerateSpid = async () => {
    try {
      setLoading(true);
      const resp = await fetchAPI(`order/${record.id}/generate-spid`, {
        method: 'POST',
        payload: {
          invoice_date: invoiceDate,
        },
      });
      message.info(
        `Success create SPID: ${
          resp
            ? resp
                .map(line => {
                  return line.id;
                })
                .join(', ')
            : 'please wait a moment'
        }`
      );
      reloadOrderList();
      hide();
      return;
    } catch (error) {
      message.error(`Failed generate SPID: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const validateMessages = {
    required: 'This field is required!',
  };

  return (
    <div style={{ gap: '15px', display: 'flex', flexDirection: 'column' }}>
      {table}
      {orderAutoPayment?.data?.isAuto && (
        <>
          <Form onFinish={postGenerateSpid} validateMessages={validateMessages}>
            <Form.Item
              label="Invoice Date"
              name="invoiceDate"
              rules={[{ required: true }]}
            >
              <DatePicker
                onChange={(date, dateString) => setInvoiceDate(dateString)}
              />
            </Form.Item>
            <Form.Item>
              <Spin spinning={loading}>
                <Button type="primary" htmlType="submit" disabled={loading}>
                  Generate SPID
                </Button>
              </Spin>
            </Form.Item>
          </Form>
        </>
      )}
    </div>
  );
};

const SupplierPaymentCreateFromOrder = ({
  record,
  reloadOrderList,
  onOpenChange,
}: {
  record: any;
  reloadOrderList: () => void;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [open, setOpen] = useState(false);

  const hide = () => {
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    // Close dropdown immediately when popover is opened
    if (newOpen) {
      onOpenChange?.(false);
    }
  };
  return (
    <Popover
      trigger={'click'}
      placement="bottom"
      visible={open}
      onVisibleChange={handleOpenChange}
      content={
        <>
          <h3>SAID-{record.id} Item To Create SPID</h3>
          <OrderItemToSelect
            record={record}
            reloadOrderList={reloadOrderList}
            hide={hide}
            visible={open}
          />
        </>
      }
    >
      <Tooltip title="Create SPID">
        <Button icon={<FileSyncOutlined />}>Create SPID</Button>
      </Tooltip>
    </Popover>
  );
};

export default SupplierPaymentCreateFromOrder;
